import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Filter, Search, X } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import {
  SHOP_PRODUCTS,
  SHOP_CATEGORIES,
  SHOP_GENDERS,
  buildWhatsAppLink,
} from "../constants";

export default function TiendaPage() {
  const [activeCategories, setActiveCategories] = useState([]); // empty = all
  const [activeGenders, setActiveGenders] = useState([]);
  const [search, setSearch] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const toggle = (list, setList, value) => {
    if (list.includes(value)) setList(list.filter((v) => v !== value));
    else setList([...list, value]);
  };

  const filtered = useMemo(() => {
    return SHOP_PRODUCTS.filter((p) => {
      if (activeCategories.length && !activeCategories.includes(p.category))
        return false;
      if (activeGenders.length && !activeGenders.includes(p.gender)) return false;
      if (
        search &&
        !`${p.name} ${p.tagline}`.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [activeCategories, activeGenders, search]);

  const clearAll = () => {
    setActiveCategories([]);
    setActiveGenders([]);
    setSearch("");
  };
  const activeCount = activeCategories.length + activeGenders.length;

  const FilterPanel = (
    <aside className="space-y-10">
      {/* Categories */}
      <div>
        <h3 className="text-[11px] uppercase tracking-[0.25em] text-ink font-semibold mb-4">
          Categoría
        </h3>
        <ul className="space-y-2.5">
          {SHOP_CATEGORIES.map((c) => {
            const checked = activeCategories.includes(c.slug);
            const count = SHOP_PRODUCTS.filter((p) => p.category === c.slug).length;
            return (
              <li key={c.slug}>
                <label
                  data-testid={`filter-cat-${c.slug}`}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <span
                    className={`relative w-4 h-4 rounded-sm border transition-colors flex items-center justify-center ${
                      checked
                        ? "bg-vino border-vino"
                        : "border-line group-hover:border-vino/50"
                    }`}
                  >
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1 5L4 8L9 2" stroke="var(--c-page)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={() => toggle(activeCategories, setActiveCategories, c.slug)}
                  />
                  <span className="flex-1 text-sm text-ink-soft group-hover:text-ink transition-colors">
                    {c.name}
                  </span>
                  <span className="text-xs text-ink-soft tabular-nums">
                    {count}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Gender */}
      <div>
        <h3 className="text-[11px] uppercase tracking-[0.25em] text-ink font-semibold mb-4">
          Género
        </h3>
        <div className="flex flex-wrap gap-2">
          {SHOP_GENDERS.map((g) => {
            const active = activeGenders.includes(g.slug);
            return (
              <button
                key={g.slug}
                type="button"
                data-testid={`filter-gender-${g.slug}`}
                onClick={() => toggle(activeGenders, setActiveGenders, g.slug)}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${
                  active
                    ? "bg-vino text-page border-vino"
                    : "bg-transparent text-ink border-line hover:border-vino/50"
                }`}
              >
                {g.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Clear */}
      {activeCount > 0 && (
        <button
          onClick={clearAll}
          data-testid="filters-clear"
          className="text-xs uppercase tracking-[0.2em] text-vino link-underline"
        >
          Limpiar filtros ({activeCount})
        </button>
      )}
    </aside>
  );

  return (
    <main data-testid="tienda-page" className="min-h-screen bg-page text-ink">
      <Navbar />

      {/* Hero header */}
      <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-12 border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <nav
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft mb-6"
            data-testid="tienda-breadcrumb"
          >
            <Link to="/" className="hover:text-vino transition-colors">
              Inicio
            </Link>
            <span>/</span>
            <span className="text-ink">Tienda</span>
          </nav>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="label-eyebrow mb-3">Catálogo Vallitas</div>
              <h1
                data-testid="tienda-title"
                className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-ink"
              >
                Tienda
              </h1>
              <p className="mt-4 max-w-xl text-base text-ink-soft">
                Explora todos los productos personalizables de Vallitas.
                Confección desde $10.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative flex-1 lg:flex-none lg:w-72">
                <Search
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft"
                />
                <input
                  type="search"
                  data-testid="tienda-search"
                  placeholder="Buscar prenda, modelo…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-full bg-sand border border-line text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:border-vino transition-colors"
                />
              </div>
              <button
                data-testid="tienda-mobile-filters-open"
                onClick={() => setMobileFiltersOpen(true)}
                className="lg:hidden inline-flex items-center gap-2 rounded-full border border-line px-4 py-3 text-sm text-ink hover:bg-sand transition-colors"
              >
                <Filter size={16} /> Filtros
                {activeCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-vino text-page text-[10px]">
                    {activeCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="relative py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Sidebar (desktop) */}
          <div className="hidden lg:block lg:col-span-3 sticky top-24 self-start">
            {FilterPanel}
          </div>

          {/* Products grid */}
          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-6">
              <p
                data-testid="tienda-results-count"
                className="text-sm text-ink-soft"
              >
                Mostrando{" "}
                <span className="text-ink font-medium">{filtered.length}</span>{" "}
                {filtered.length === 1 ? "producto" : "productos"}
              </p>
            </div>

            {filtered.length === 0 ? (
              <div
                data-testid="tienda-empty"
                className="paper-card rounded-3xl p-12 text-center"
              >
                <p className="font-heading text-3xl text-ink mb-3">
                  Nada coincide con tu búsqueda
                </p>
                <p className="text-sm text-ink-soft mb-6">
                  Limpia los filtros o cuéntanos qué buscas y lo confeccionamos a tu medida.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={clearAll}
                    className="rounded-full border border-vino/40 text-ink px-6 py-3 text-sm font-medium hover:bg-sand transition-colors"
                  >
                    Limpiar filtros
                  </button>
                  <a
                    href={buildWhatsAppLink("Hola, busco una prenda específica que no veo en la tienda.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full bg-vino text-page px-6 py-3 text-sm font-medium hover:bg-vino-deep transition-colors"
                  >
                    Pedir por WhatsApp
                  </a>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
                {filtered.map((p) => {
                  const Wrapper = p.href ? Link : "div";
                  const wrapperProps = p.href ? { to: p.href } : {};
                  return (
                    <article
                      key={p.id}
                      data-testid={`shop-product-${p.id}`}
                      className="group flex flex-col"
                    >
                      <Wrapper
                        {...wrapperProps}
                        className="img-zoom relative rounded-2xl sm:rounded-[1.75rem] overflow-hidden aspect-[3/4] bg-sand block"
                      >
                        <img
                          src={p.image}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        {p.badge && (
                          <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-page/90 backdrop-blur-md px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-vino font-medium border border-line">
                            {p.badge}
                          </span>
                        )}
                      </Wrapper>
                      <div className="mt-4 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-heading text-lg sm:text-xl text-ink leading-tight truncate">
                            {p.name}
                          </h3>
                          <p className="text-xs text-ink-soft italic mt-0.5 truncate">
                            {p.tagline}
                          </p>
                        </div>
                        {p.href ? (
                          <Link
                            to={p.href}
                            data-testid={`shop-cta-${p.id}`}
                            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-vino/40 text-vino group-hover:bg-vino group-hover:text-page transition-colors"
                            aria-label={`Ver ${p.name}`}
                          >
                            <ArrowUpRight size={14} />
                          </Link>
                        ) : (
                          <a
                            href={buildWhatsAppLink(`Hola, me interesa ${p.name}.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`shop-cta-${p.id}`}
                            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-vino/40 text-vino group-hover:bg-vino group-hover:text-page transition-colors"
                            aria-label={`Cotizar ${p.name}`}
                          >
                            <ArrowUpRight size={14} />
                          </a>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile filters drawer */}
      {mobileFiltersOpen && (
        <div
          data-testid="tienda-mobile-filters-drawer"
          className="lg:hidden fixed inset-0 z-50 flex"
          onClick={() => setMobileFiltersOpen(false)}
        >
          <div className="flex-1 bg-ink/40 backdrop-blur-sm" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-[85%] max-w-sm bg-page border-l border-line p-6 overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl text-ink">Filtros</h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                aria-label="Cerrar filtros"
                className="text-ink-soft hover:text-ink"
                data-testid="tienda-mobile-filters-close"
              >
                <X size={22} />
              </button>
            </div>
            {FilterPanel}
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="mt-10 w-full rounded-full bg-vino text-page py-3 text-sm font-medium"
            >
              Ver {filtered.length} productos
            </button>
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}
